import { getCurrentTheme } from '@mc-alberta/alcapone'
import { vuex as analyticsPlugin } from '@mc-alberta/analytics'
import * as i18nData from '@mc-alberta/i18n-data'
import { lumber } from '@mc-alberta/lumber'
import { vuexPlugin as switchboardPlugin } from '@mc-alberta/switchboard'
import axios from 'axios'
import Vue from 'vue'
import Vuex from 'vuex'

import akamai from '@/api/akamai'
import * as constants from '@/constants'
import authentication from '@/store/modules/authentication'
import cards from '@/store/modules/cards'
import identity from '@/store/modules/identity'
import shippingAddresses from '@/store/modules/shippingAddresses'
import user from '@/store/modules/user'

import * as types from '@/store/mutation-types'
import errorHandlingMap from '@/utils/errorHandlingMap'
import isDevMode from '@/utils/isDevMode'
import { fetchPostMessage } from '@/utils/postMessage'
import { getTraceId } from '@/utils/traceId'

/**
 * @fileoverview This is the index.js for Vuex, our state manager where we
 * have our top level state object and all of our dependent state modules.
 * Most of the interesting stuff in the application will be happening here.
 * This is where we have our top level getters, actions, and mutations.
 * If you want to know what the application can do, this is a decent place
 * to start. Also check App.vue to see how we get some of our initial data.
 *
 * @note actions, getters, and mutations exported separately for unit testing
 */

Vue.use(Vuex)

const defaultMessagePageContent = {
  buttonTarget: constants.ROUTES.ENTRY,
  secondaryButtonTarget: constants.ROUTES.ENTRY,
  buttonText: 'button.ok',
  secondaryButtonText: '',
  errorId: `${constants.DATA_TEST.ERROR}-default`,
  header: 'error.heading',
  message: 'error.subheading',
  iconClass: 'icon-error icon-size-lg icon-bg-red'
}

const state = {
  analyticsFeature: constants.ANALYTICS.FEATURES.DEFAULT,
  correlationId: '',
  channel: 'web',
  cookieConsent: true, // default to US style true
  serviceLayerError: null,
  action: constants.ACTION.MANAGE,
  authorization: null,
  analyticsFlow: '',
  isInitialized: false,
  isLocaleSelectorActive: false,
  isLocaleSelectorSelected: false,
  actionIsPending: false,
  interceptUnauthorizedError: true,
  showSessionExpireNotice: false,
  registrationData: null,
  confirmationModalConfig: { visible: false, routeTo: null },
  loadingQueue: [],
  loadingSuccessAnimationQueue: [],
  messagePageContent: defaultMessagePageContent,
  mustAcceptPolicy: false,
  mustReaccept: false,
  postLoginPath: null,
  skipUnprovisionedCards: false,
  appTheme: getCurrentTheme(),
  webAuthnInfo: {},
  stepMap: { activeStep: 0 }
}

export const getters = {
  /**
   * Checks if there is a service layer error and returns the response object
   * @returns {object} error response object, or void if no error
   */
  serviceErrorResponse: (state) => (source) => {
    if (!state.serviceLayerError) {
      return
    }

    let response = state.serviceLayerError.response
    if (source) {
      const request = state.serviceLayerError.request
      const url = request.responseURL || request.url || ''
      const pattern = new RegExp(source, 'i')

      if (!pattern.test(url)) {
        response = undefined
      }
    }

    return response
  },

  /**
   * Returns sorted shipping addresses by date last used.
   *
   * @param {Object} state - the current state (of shippingAddresses)
   * @returns {Array} the sorted shippingAddresses
   */
  sortedShippingAddresses(state) {
    const sorter = (a, b) => b.lastUsedTime.getTime() - a.lastUsedTime.getTime()
    return [...state.shippingAddresses].sort(sorter)
  },

  /**
   * Returns mustAcceptPolicy, used for blocking navigation and to direct user to tnc
   * @returns {function} returns function in order to update computed value
   */
  userMustAcceptPolicy: (state) => () => state.mustAcceptPolicy,

  /**
   * Returns type of reacceptance, major or minor, if none, will return false
   * @returns {function} returns function in order to update computed value
   */
  typeOfReacceptance: (state) => () => state.mustReaccept,

  /**
   * Returns terms url, used to link to terms page with correct locale
   * @returns {String}
   */
  termsUrl(_state, getters) {
    // The url for fr-CA is different than for all other locales. Architecture is currently
    // negotiating with the external team that manages these urls. Until it is decided whether
    // or not the external terms page can consume the url, this is a temporary approach
    // TODO: Remove once the terms page can consume the locale from the url
    if (getters.locale === 'fr-CA') {
      return constants.TERMS_URL_FRENCH_CANADA
    }

    return process.env.TERMS_URL.replace(
      constants.TNC_PRIVACY_DEFAULT_LOCALE,
      getters.locale.toLowerCase()
    )
  },

  /**
   * Returns privacy url, used to link to privacy page with correct locale
   * @returns {String}
   */
  privacyUrl(_state, getters) {
    // The url for fr-CA is different than for all other locales. Architecture is currently
    // negotiating with the external team that manages these urls. Until it is decided whether
    // or not the external terms page can consume the url, this is a temporary approach
    // TODO: Remove once the privacy page can consume the locale from the url
    if (!i18nData.isCountryLive(getters.locale) && i18nData.isCountrySupported(getters.locale)) {
      return process.env.PRIVACY_URL_UNSUPPORTED_COUNTRY
    } else if (getters.locale === 'fr-CA') {
      return constants.PRIVACY_URL_FRENCH_CANADA
    } else {
      return process.env.PRIVACY_URL.replace(
        constants.TNC_PRIVACY_DEFAULT_LOCALE,
        getters.locale.toLowerCase()
      )
    }
  },

  /**
   * Returns terms url, used to compliance setting
   * @returns {String}
   */
  complianceTermsUrl(_state, getters) {
    return getters.locale === 'fr-CA'
      ? constants.COMPLIANCE_TERMS_URL_FRENCH_CANADA
      : constants.COMPLIANCE_TERMS_URL
  },

  /**
   * Returns privacy url, used to compliance setting
   * @returns {String}
   */
  compliancePrivacyUrl(_state, getters) {
    if (!i18nData.isCountryLive(getters.locale) && i18nData.isCountrySupported(getters.locale)) {
      return process.env.PRIVACY_URL_UNSUPPORTED_COUNTRY
    } else if (getters.locale === 'fr-CA') {
      return constants.COMPLIANCE_PRIVACY_URL_FRENCH_CANADA
    } else {
      return constants.COMPLIANCE_PRIVACY_URL
    }
  },

  countries: (state, getters) => i18nData.getTranslatedCountries(getters.locale),

  isRegisterUserFlow: (state) => state.action === constants.ACTION.REGISTER,

  /**
   * @param {Object} state
   * @returns {Object} active loader
   */
  activeLoader(state) {
    return state.loadingQueue?.[0]
  },

  /**
   * @param {Object} state
   * @returns {Boolean}
   */
  isLoading(state) {
    return state.loadingQueue?.length > 0
  },

  /**
   * Whether the active loader has been requested to stop loading with an animation
   *
   * @param {Object} state
   * @param {Object} getters
   * @returns {Boolean}
   */
  waitingForLoaderSuccessAnimation(state, getters) {
    const activeLoaderId = getters.activeLoader?.id
    if (!activeLoaderId) return false

    return state.loadingSuccessAnimationQueue.includes(activeLoaderId)
  }
}

export const actions = {
  /**
   * Starts Account management application and sets initial datas
   *
   * @param {Vuex} store
   * @param {URLSearchParams} [params] instance of URLSearchParams containing query string params
   */
  async init({ commit, dispatch }, { params = new URLSearchParams(), webAuthRes } = {}) {
    // localize as early as possible so we can potentially translate things like loading screens
    dispatch('setInitialLocale', params.get('locale'))
    axios.defaults.headers['src-client-id'] = constants.CLIENT_ID

    // Pass the traceId to the backend calls as x-src-trace-id since it is not an emvco parameter
    axios.defaults.headers['x-src-trace-id'] = getTraceId()

    if (params.get('verificationId') && params.get('verificationToken')) {
      dispatch('startLoader', constants.LOADERS.APP_INIT)

      const emailConfirmationData = {
        idVerificationId: params.get('verificationId'),
        verificationMode: 'OUTSESSION'
      }
      dispatch('setVerificationData', emailConfirmationData)
      dispatch('setAction', constants.ACTION.EMAIL_CONFIRMATION)

      commit(types.SET_INTERCEPT_UNAUTHORIZED_ERROR, false)
      await dispatch('validateVerificationCode', params.get('verificationToken'))
      commit(types.SET_INTERCEPT_UNAUTHORIZED_ERROR, true)
    } else {
      dispatch('setAction', constants.ACTION.MANAGE)
      const dcfData = (await fetchPostMessage(params)) || {}
      const storedAuthorization = dcfData.authorization || sessionStorage.getItem('authorization')

      if (storedAuthorization) {
        dispatch('startLoader', constants.LOADERS.APP_INIT)
        await dispatch('initProfileFlow', { storedAuthorization, dcfData })

        if (!dcfData.authorization) {
          dispatch('stopLoader', constants.LOADERS.APP_INIT)
        }
      }
    }

    commit(types.SET_WEBAUTHN_INFO, webAuthRes)
    commit(types.SET_INITIALIZED, true)
  },

  async initProfileFlow({ state, commit, dispatch, getters }, { storedAuthorization, dcfData }) {
    const storedCorrelationId = dcfData.correlationId || sessionStorage.getItem('correlationId')
    await dispatch('setCorrelationId', storedCorrelationId)
    await dispatch('setAuthorization', storedAuthorization)
    await dispatch('loadCustomerProfile')
    if ((state.serviceLayerError || !getters.isLoggedIn) && !dcfData.authorization) {
      // most likely an expired authorization token, lets remove it
      dispatch('removeAuthorization')
      dispatch('clearServiceLayerError')
    }
    commit(types.SET_FLOW, constants.ANALYTICS.FLOW.ACCOUNT_MANAGE)
  },

  /**
   * Start a loader by adding it to a queue.
   *
   * You can pass any details to this loader as long as it has a unique id that's used to stop it.
   * This allows for any ordering of multiple loaders as well as easy debugging for when one loader
   * starts and another ends.
   *
   * @param {Vuex} store
   * @param {Object} loader
   * @param {String} loader.id
   */
  startLoader({ commit }, loader) {
    if (!loader?.id) return

    commit(types.START_LOADER, loader)
  },

  /**
   * Stops a single loader if the specified id matches a loader in the queue
   *
   * @param {Vuex} store
   * @param {Object} loader
   * @param {String} loader.id
   */
  stopLoader({ state, commit }, loader) {
    if (!loader?.id) return

    if (state.loadingSuccessAnimationQueue?.includes(loader.id)) {
      commit(types.STOP_LOADER_SUCCESS_ANIMATION_WITH_ID, loader.id)
    }

    commit(types.STOP_LOADER_WITH_ID, loader.id)
  },

  /**
   * Stops the active loader or the loader given after it's done with its success animation
   *
   * i.e. this currently causes a green checkmark to be displayed after loading,
   * indicating that processing has completed successfully.
   *
   * We should try to always pass in a loader versus leaving the params empty unless it can't
   * be avoided. An appropriate scenario to leave params empty is in a reusable context such as
   * within a composable where the loader(s) is (are) dynamic and could be anything.
   *
   * @param {Vuex} store
   * @param {Object} loader
   * @param {String} loader.id
   * @returns
   */
  stopLoaderWithSuccess({ commit, getters }, loader) {
    const loaderId = loader?.id || getters.activeLoader?.id
    if (!loaderId) return

    commit(types.START_LOADER_SUCCESS_ANIMATION, loaderId)
  },

  /**
   * Stops all loaders in queue
   *
   * We should try to avoid using this unless absolutely necessary and instead stop the loader
   * using `stopLoader(...)`. An appropriate scenario to use this is in a reusable context such as
   * within a composable where the loader(s) is (are) dynamic and could be anything.
   */
  stopAllLoaders({ commit }) {
    commit(types.STOP_ALL_LOADERS)
  },

  /**
   * Store service level errors to be checked later
   *
   * @param {Vuex} vuex
   * @param {object} error the error been thrown
   */
  setServiceLayerError(store, error) {
    const { commit } = store
    const { request, response } = error
    const isValidationAborted = error.code === constants.VALIDATION.ABORT_CODE
    // check if its a real http or api timeout error, otherwise allow it to bubble
    if ((request && response) || isValidationAborted) {
      const { status } = response || {}
      if (errorHandlingMap[status]) {
        errorHandlingMap[status](store, error)
      }

      commit(types.SET_SERVICE_LAYER_ERROR, error)
    } else {
      throw error
    }
  },

  /**
   * Removes the existing API error.
   *
   * @param {Vuex} store
   */
  clearServiceLayerError({ commit }) {
    commit(types.SET_SERVICE_LAYER_ERROR, null)
  },

  /**
   * Commits the given authorization to the store. Sets the `Authorization` header if applicable.
   *
   * @param {Vuex} store
   * @param {String} authorization
   */
  setAuthorization({ commit }, authorization) {
    if (!authorization) {
      return
    }

    axios.defaults.headers.Authorization = `Bearer ${authorization}`
    sessionStorage.setItem('authorization', authorization)
    commit(types.SET_AUTHORIZATION, authorization)
  },

  /**
   * Set the session id
   *
   * This is also being used as an axios response interceptor in main.js
   *
   * @param {Vuex} store
   * @param {String} newCorrelationId
   */
  async setCorrelationId({ state, commit }, newCorrelationId) {
    if (!newCorrelationId) {
      return
    }

    const oldCorrelationId = state.correlationId

    axios.defaults.headers['src-correlation-id'] = newCorrelationId
    sessionStorage.setItem('correlationId', newCorrelationId)
    commit(types.SET_CORRELATION_ID, newCorrelationId)
    lumber.setHeaderConstants({ correlationId: newCorrelationId })

    if (oldCorrelationId) {
      lumber.info({
        event: 'CORRELATION_ID_CHANGED',
        details: {
          message: `Correlation ID changed from ${oldCorrelationId} to ${newCorrelationId}.`
        }
      })
    }
  },

  /**
   * Removes the authorization from the header and store.
   *
   * @param {Vuex} store
   * @param {String} authorization
   */
  removeAuthorization({ commit }) {
    delete axios.defaults.headers.Authorization
    sessionStorage.removeItem('authorization')
    commit(types.SET_AUTHORIZATION, null)
  },

  /**
   * Updates messagePageContent in store.
   *
   * @param {Vuex} store
   * @param {object} messagePageContent
   */
  setMessagePageContent({ commit }, messagePageContent) {
    commit(types.SET_MESSAGE_PAGE_CONTENT, messagePageContent)
  },

  /**
   * Updates appTheme in store.
   *
   * @param {Vuex} store
   * @param {String} appTheme
   */
  setAppTheme({ commit }, appTheme) {
    commit(types.SET_APP_THEME, appTheme)
  },

  /**
   * Reset state in between analytics submissions
   *
   * @param {Vuex} store
   */
  resetAnalyticsState({ commit }) {
    commit(types.SET_LOCALE_SELECTOR_SELECTED, false)
  },

  /**
   * Resets the message page content back to default
   *
   * @param {Vuex} store
   */
  resetMessagePageContent({ commit }) {
    commit(types.SET_MESSAGE_PAGE_CONTENT, defaultMessagePageContent)
  },

  /**
   * Update app action
   *
   * @param {Vuex} store
   * @param {object} action
   */
  setAction({ commit }, action) {
    commit(types.SET_ACTION, action)

    if (action === constants.ACTION.MANAGE) {
      commit(types.SET_FLOW, constants.ANALYTICS.FLOW.ACCOUNT_MANAGE)
    } else if (action === constants.ACTION.REGISTER) {
      commit(types.SET_FLOW, constants.ANALYTICS.FLOW.ACCOUNT_REGISTRATION)
    }
  },

  /**
   * Store registration data across registration flow
   *
   * @param {Vuex} store
   * @param {object} registrationData
   */
  setRegistrationData({ commit }, registrationData) {
    if (registrationData !== null) {
      const { emailAddress } = registrationData.consumer
      commit(types.SET_USER_ID, emailAddress)
    }
    commit(types.SET_REGISTRATION_DATA, registrationData)
  },

  /**
   * Incrememnt step
   *
   * @param {Vuex} store
   * @param {String} routeName
   */
  addStep({ state, commit }, routeName) {
    if (!Object.prototype.hasOwnProperty.call(state.stepMap, routeName)) {
      commit(types.STEP_MAP, routeName)
    }
  },

  /**
   * Logs the user out by clearing the Authorization header and resetting the consumer object.
   *
   * @param {Vuex} store
   */
  logout({ commit, dispatch }) {
    dispatch('removeAuthorization')

    // refresh the akamai server ID so it doesnt become stale
    akamai.getTag()

    commit(types.SET_MUST_ACCEPT_POLICY)
    commit(types.SET_MUST_REACCEPT)
    commit(types.SET_POST_LOGIN_PATH)
    commit(types.SET_SKIP_UNPROVISION, false)

    // all modules that need to be reset to default
    // during logout must implement a reset action. ~Ronald
    dispatch('reset')
  }
}

export const mutations = {
  [types.SET_APP_THEME](state, appTheme) {
    state.appTheme = appTheme
  },

  [types.SET_SERVICE_LAYER_ERROR](state, serviceLayerError) {
    state.serviceLayerError = serviceLayerError
  },

  [types.SET_REGISTRATION_DATA](state, registrationData) {
    state.registrationData = registrationData
  },

  [types.SET_CORRELATION_ID](state, correlationId) {
    state.correlationId = correlationId
  },

  [types.SET_ACTION](state, action) {
    state.action = action
  },

  [types.SET_ANALYTICS_FEATURE](state, analyticsFeature = 'default') {
    state.analyticsFeature = analyticsFeature
  },

  [types.SET_AUTHORIZATION](state, authorization) {
    state.authorization = authorization
  },

  [types.SET_FLOW](state, flow) {
    state.analyticsFlow = flow
  },

  [types.SET_INITIALIZED](state, isInitialized) {
    state.isInitialized = isInitialized
  },

  [types.STEP_MAP](state, routeName) {
    state.stepMap.activeStep += 1
    const newStep = { [routeName]: state.stepMap.activeStep }

    state.stepMap = { ...state.stepMap, ...newStep }
  },

  [types.RESET_STEP_MAP](state) {
    state.stepMap = { activeStep: 0 }
  },

  [types.SET_MESSAGE_PAGE_CONTENT](state, messagePageContent) {
    state.messagePageContent = { ...state.messagePageContent, ...messagePageContent }
  },

  [types.SET_INTERCEPT_UNAUTHORIZED_ERROR](state, intercept) {
    state.interceptUnauthorizedError = intercept
  },

  [types.SET_SESSION_EXPIRED_NOTICE](state, show) {
    state.showSessionExpireNotice = show
  },

  [types.SET_CONFIRMATION_MODAL_CONFIG](state, { visible = false, routeTo = null } = {}) {
    state.confirmationModalConfig = { visible, routeTo }
  },

  [types.SET_ACTION_IS_PENDING](state, loading) {
    state.actionIsPending = loading
  },

  /**
   * Sets mustAcceptPolicy
   * @param {Object} state
   * @param {Boolean} mustAcceptPolicy
   */
  [types.SET_MUST_ACCEPT_POLICY](state, mustAcceptPolicy = false) {
    state.mustAcceptPolicy = mustAcceptPolicy
  },

  /**
   * Sets mustReaccept
   *
   * @param {Object} state
   * @param {Boolean} difference
   */
  [types.SET_MUST_REACCEPT](state, difference = false) {
    state.mustReaccept = difference
  },

  /**
   * Whether or not user clicked to open locale selector since last analytics submission
   * @param {Object} state
   * @param {Boolean} val
   */
  [types.SET_LOCALE_SELECTOR_SELECTED](state, val) {
    state.isLocaleSelectorSelected = val
  },

  /**
   * Whether or not user clicked to open locale selector since last analytics submission
   * @param {Object} state
   * @param {Boolean} val
   */
  [types.SET_LOCALE_SELECTOR_ACTIVE](state, val) {
    state.isLocaleSelectorActive = val
  },

  /**
   * stores the postLoginPath to route to
   */
  [types.SET_POST_LOGIN_PATH](state, path) {
    state.postLoginPath = path
  },

  /**
   * whether or not to skip unprovision flow
   */
  [types.SET_SKIP_UNPROVISION](state, val) {
    state.skipUnprovisionedCards = val
  },
  /**
   * set web Auth and spc detection info
   */
  [types.SET_WEBAUTHN_INFO](state, data) {
    state.webAuthnInfo = data
  },
  [types.START_LOADER](state, loader) {
    state.loadingQueue.push(loader)
  },
  [types.STOP_ALL_LOADERS](state) {
    state.loadingQueue.splice(0)
    state.loadingSuccessAnimationQueue.splice(0)
  },
  /**
   * Remove the first loader with specified id.
   *
   * We should only remove one loader at a time even if there are multiple loaders with the same id
   * to ensure that, whenever we stop a loader by id, we're not going to accidentally stop the
   * wrong loader.
   */
  [types.STOP_LOADER_WITH_ID](state, loaderId) {
    const loaderIndex = state.loadingQueue.findIndex(({ id }) => id === loaderId)
    if (loaderIndex < 0) return

    state.loadingQueue.splice(loaderIndex, 1)
  },
  [types.START_LOADER_SUCCESS_ANIMATION](state, loaderId) {
    state.loadingSuccessAnimationQueue.push(loaderId)
  },
  [types.STOP_LOADER_SUCCESS_ANIMATION_WITH_ID](state, loaderId) {
    const loaderIndex = state.loadingSuccessAnimationQueue.indexOf(loaderId)
    if (loaderIndex < 0) return

    state.loadingSuccessAnimationQueue.splice(loaderIndex, 1)
  }
}

// Analytics meta information
const _analyticsContextMapper = (state) => ({
  analyticsFeature: state.analyticsFeature,
  consumerId: state.user.consumer.id,
  consumerLanguage: state.user.consumer.languageCode,
  consumerCountry: state.user.consumer.countryCode,
  experiments: state.user.experiments,
  flow: state.analyticsFlow,
  channel: state.channel,
  clientId: constants.CLIENT_ID,
  srcCorrelationId: state.correlationId,
  traceId: getTraceId(),
  cardBrands: [],
  appTheme: {
    mode: state.appTheme,
    appliedTheme: document.documentElement.getAttribute('dark') ? 'DARK' : 'LIGHT'
  },
  isPlatformAuth: state.webAuthnInfo?.isPlatformAuth,
  isWebAuthNSupported: state.webAuthnInfo?.isWebAuthNSupported,
  isSPCSupported: state.webAuthnInfo?.isSPCSupported,
  isAuthScriptSuccessful: state.webAuthnInfo?.isAuthScriptSuccessful
})

export const _switchboardContextMapper = (state) => ({
  channel: state.channel,
  locale: user.getters.locale(user.state)
})

export const modules = { authentication, cards, identity, shippingAddresses, user }

export default new Vuex.Store({
  state,
  getters,
  actions,
  mutations,
  modules,
  strict: isDevMode,
  plugins: [analyticsPlugin(_analyticsContextMapper), switchboardPlugin(_switchboardContextMapper)]
})
